import { useState, useEffect } from 'react'
import { useParams, Link } from 'react-router-dom'
import { getSharedResult } from './services/api'
import type { TestResult, ModeResult, DimensionResult, OverallStabilityType, DimensionId, StabilityType, TypeCode } from '@mindtype/shared'
import { TYPE_INTERPRETATIONS, TYPE_COLORS } from '@mindtype/shared'
import { ModeOverview } from './components/ModeOverview'
import { DimensionDetails } from './components/DimensionDetails'
import { TypeBrowser } from './components/TypeBrowser'
import { TypeDetailModal } from './components/TypeDetailModal'
import { InterpersonalModule } from './components/InterpersonalModule'
import { ShareCard } from './components/ShareCard'
import { SelfKnowledgeModule } from './components/SelfKnowledgeModule'
import { CareerDevelopmentModule } from './components/CareerDevelopmentModule'

// 标签配置
const TABS = [
  { id: 'result', name: '测试结果' },
  { id: 'self', name: '自我认知' },
  { id: 'career', name: '职业发展' },
  { id: 'interpersonal', name: '人际关系' },
] as const

type TabId = typeof TABS[number]['id']

export function SharedResultPage() {
  const { token } = useParams<{ token: string }>()
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [result, setResult] = useState<TestResult | null>(null)
  const [selectedTypeCode, setSelectedTypeCode] = useState<string | null>(null)
  const [showShareCard, setShowShareCard] = useState(false)
  const [activeTab, setActiveTab] = useState<TabId>('result')

  useEffect(() => {
    async function fetchResult() {
      if (!token) {
        setError('无效的分享链接')
        setLoading(false)
        return
      }

      const response = await getSharedResult(token)

      if (response.success && response.data) {
        const apiResult = response.data.result
        const modes: ModeResult = {
          natural: apiResult.modes.natural as any,
          work: apiResult.modes.work as any,
          stress: apiResult.modes.stress as any,
        }

        const dimensions: DimensionResult[] = apiResult.dimensions.map(dim => ({
          dimensionId: dim.dimensionId as DimensionId,
          stability: dim.stability as StabilityType,
          natural: {
            poleACount: dim.natural.poleACount,
            poleBCount: dim.natural.poleBCount,
            result: '',
            resultLetter: dim.natural.resultLetter,
            ratio: `${dim.natural.poleACount}:${dim.natural.poleBCount}`,
            isWeak: dim.natural.isWeak,
          },
          work: {
            poleACount: dim.work.poleACount,
            poleBCount: dim.work.poleBCount,
            result: '',
            resultLetter: dim.work.resultLetter,
            ratio: `${dim.work.poleACount}:${dim.work.poleBCount}`,
            isWeak: dim.work.isWeak,
          },
          stress: {
            poleACount: dim.stress.poleACount,
            poleBCount: dim.stress.poleBCount,
            result: '',
            resultLetter: dim.stress.resultLetter,
            ratio: `${dim.stress.poleACount}:${dim.stress.poleBCount}`,
            isWeak: dim.stress.isWeak,
          },
        }))

        const overallStability = apiResult.overallStability.type as OverallStabilityType
        const stableAxes = apiResult.overallStability.stableAxes as DimensionId[]
        const flexibleAxes = apiResult.overallStability.flexibleAxes.map(f => ({
          dimension: f.dimension as DimensionId,
          pattern: f.pattern as StabilityType,
        }))

        setResult({
          responseId: response.data.id,
          version: '1.0',
          timestamp: response.data.createdAt,
          duration: 0,
          answers: [],
          dimensions,
          modes,
          overallStability,
          stableAxes,
          flexibleAxes,
          crossValidation: {
            rate: apiResult.crossValidation.rate,
            reliable: apiResult.crossValidation.reliable as any,
            details: [],
            summary: {
              totalChecks: apiResult.crossValidation.totalChecks,
              consistentChecks: apiResult.crossValidation.consistentChecks,
              inconsistentChecks: apiResult.crossValidation.totalChecks - apiResult.crossValidation.consistentChecks,
            },
          },
        })
      } else {
        setError('无法加载分享结果，链接可能已失效')
      }

      setLoading(false)
    }

    fetchResult()
  }, [token])

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="inline-block w-8 h-8 border-4 border-blue-500 border-t-transparent rounded-full animate-spin mb-4"></div>
          <p className="text-gray-500">加载中...</p>
        </div>
      </div>
    )
  }

  if (error || !result) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
        <div className="bg-white rounded-xl border border-gray-200 p-8 max-w-md text-center">
          <div className="text-4xl mb-4">:(</div>
          <h1 className="text-xl font-semibold text-gray-900 mb-2">
            {error || '加载失败'}
          </h1>
          <p className="text-gray-500 mb-6">
            请检查链接是否正确，或联系分享者获取新链接
          </p>
          <Link
            to="/"
            className="inline-block px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
          >
            开始我的测试
          </Link>
        </div>
      </div>
    )
  }

  const userNaturalType = result.modes.natural as TypeCode
  const typeInfo = TYPE_INTERPRETATIONS[userNaturalType]
  const colorConfig = TYPE_COLORS[userNaturalType]

  return (
    <div className="min-h-screen py-8 px-4" style={{ backgroundColor: `${colorConfig.primary}03` }}>
      <div className="max-w-3xl mx-auto">
        {/* 分享提示横幅 */}
        <div
          className="rounded-lg p-4 text-center mb-6"
          style={{ backgroundColor: `${colorConfig.primary}10`, border: `1px solid ${colorConfig.primary}20` }}
        >
          <p style={{ color: colorConfig.primary }}>
            这是一份分享的认知风格测试结果
          </p>
        </div>

        {/* ========== 页面头部 ========== */}
        <header className="text-center py-4 mb-6">
          <h1 className="text-2xl font-semibold text-gray-900">认知风格分析报告</h1>
        </header>

        {/* ========== 类型展示卡片 ========== */}
        <div
          className="rounded-2xl p-8 text-center mb-6"
          style={{ backgroundColor: `${colorConfig.primary}08`, border: `1px solid ${colorConfig.primary}15` }}
        >
          <div className="text-sm mb-3 tracking-wide" style={{ color: colorConfig.primary }}>认知类型</div>
          <div
            className="inline-block px-5 py-2.5 rounded-lg mb-4 shadow-sm"
            style={{ backgroundColor: colorConfig.primary }}
          >
            <span
              className="text-3xl font-bold tracking-widest font-mono"
              style={{ color: colorConfig.text }}
            >
              {userNaturalType}
            </span>
          </div>
          <h2
            className="text-2xl font-semibold mb-3"
            style={{ color: colorConfig.primary }}
          >
            {typeInfo?.name}
          </h2>
          <p className="text-gray-600 text-sm italic">"{typeInfo?.tagline}"</p>
        </div>

        {/* ========== 标签导航 ========== */}
        <nav className="flex justify-center mb-6">
          <div
            className="inline-flex rounded-lg p-1"
            style={{ backgroundColor: `${colorConfig.primary}10` }}
          >
            {TABS.map((tab) => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className="px-5 py-2 rounded-md text-sm font-medium transition-all duration-200"
                style={{
                  backgroundColor: activeTab === tab.id ? colorConfig.primary : 'transparent',
                  color: activeTab === tab.id ? colorConfig.text : colorConfig.primary,
                }}
              >
                {tab.name}
              </button>
            ))}
          </div>
        </nav>

        {/* ========== 标签内容 ========== */}
        <div className="space-y-6">
          {/* 测试结果 */}
          {activeTab === 'result' && (
            <>
              {/* 三层模式总览 */}
              <ModeOverview modes={result.modes} onTypeClick={setSelectedTypeCode} colorConfig={colorConfig} />

              {/* 四维度详情 */}
              <DimensionDetails dimensions={result.dimensions} colorConfig={colorConfig} />
            </>
          )}

          {/* 自我认知 */}
          {activeTab === 'self' && (
            <SelfKnowledgeModule
              typeCode={userNaturalType}
              modes={result.modes}
              dimensions={result.dimensions}
              overallStability={result.overallStability}
              stableAxes={result.stableAxes}
              flexibleAxes={result.flexibleAxes}
              colorConfig={colorConfig}
            />
          )}

          {/* 职业发展 */}
          {activeTab === 'career' && (
            <CareerDevelopmentModule
              typeCode={userNaturalType}
              colorConfig={colorConfig}
            />
          )}

          {/* 人际关系 */}
          {activeTab === 'interpersonal' && (
            <InterpersonalModule
              userType={userNaturalType}
              colorConfig={colorConfig}
            />
          )}
        </div>

        {/* ========== 探索所有类型 ========== */}
        <div className="mt-6">
          <TypeBrowser userType={userNaturalType} colorConfig={colorConfig} />
        </div>

        {/* 类型详情模态框 */}
        <TypeDetailModal
          typeCode={selectedTypeCode || ''}
          isOpen={!!selectedTypeCode}
          onClose={() => setSelectedTypeCode(null)}
          isUserType={selectedTypeCode === userNaturalType}
        />

        {/* ========== 邀请好友测试 ========== */}
        <div
          className="rounded-xl p-6 text-center mt-6"
          style={{ backgroundColor: `${colorConfig.primary}05`, border: `1px solid ${colorConfig.primary}15` }}
        >
          <h3 className="text-base font-semibold mb-2" style={{ color: colorConfig.primary }}>
            想知道你和朋友/同事的相处模式？
          </h3>
          <p className="text-sm text-gray-600 mb-4">
            邀请TA也来测试，对照你们的类型组合，看看是天然契合、互补搭配，还是需要磨合。
          </p>
          <button
            onClick={() => setShowShareCard(true)}
            className="px-6 py-2.5 rounded-lg font-medium transition-all hover:opacity-90"
            style={{ backgroundColor: colorConfig.primary, color: colorConfig.text }}
          >
            生成分享卡片
          </button>
        </div>

        {/* 底部 CTA */}
        <div
          className="rounded-xl p-6 text-center mt-6"
          style={{ backgroundColor: `${colorConfig.primary}05`, border: `1px solid ${colorConfig.primary}15` }}
        >
          <h2 className="text-lg font-semibold text-gray-900 mb-2">
            想探索你自己的认知风格？
          </h2>
          <p className="text-gray-500 mb-4 text-sm">
            回到首页了解框架介绍，或直接开始测试
          </p>
          <Link
            to="/"
            className="inline-block px-8 py-3 rounded-lg font-medium transition-all hover:opacity-90"
            style={{ backgroundColor: colorConfig.primary, color: colorConfig.text }}
          >
            前往首页
          </Link>
        </div>

        {/* 版权声明 */}
        <footer className="text-center text-xs text-gray-400 py-6 mt-8" style={{ borderTop: `1px solid ${colorConfig.primary}10` }}>
          <p>© 2024 MindType</p>
          <p className="mt-1">内容受版权保护，未经授权禁止商业使用</p>
        </footer>
      </div>

      {/* 分享卡片模态框 */}
      {showShareCard && (
        <ShareCard result={result} onClose={() => setShowShareCard(false)} />
      )}
    </div>
  )
}
